import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(0);
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts) => {
    const maxAttempts = 5;
    const token = localStorage.getItem("token");

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPaymentData(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        // Update local user data
        const userResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        localStorage.setItem("user", JSON.stringify(userResponse.data));
        toast.success("Payment successful! Credits added to your account.");
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Payment status check failed", error);
      setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <Card className="bg-background-paper border-border">
          <CardContent className="p-8 text-center">
            {status === "checking" && (
              <>
                <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
                <h2 className="font-heading font-bold text-xl text-text-primary mb-2">
                  Processing Payment...
                </h2>
                <p className="text-text-secondary">
                  Please wait while we confirm your payment.
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="w-20 h-20 rounded-full bg-accent-success/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-accent-success" />
                </div>
                <h2 className="font-heading font-bold text-xl text-text-primary mb-2">
                  Payment Successful!
                </h2>
                <p className="text-text-secondary mb-6">
                  Your credits have been added to your account.
                </p>
                {paymentData && (
                  <div className="p-4 rounded-lg bg-background-subtle mb-6">
                    <div className="text-2xl font-bold text-primary">
                      ${paymentData.amount}
                    </div>
                    <div className="text-sm text-text-muted uppercase">
                      {paymentData.currency}
                    </div>
                  </div>
                )}
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="w-full bg-primary hover:bg-primary-hover text-white"
                >
                  Go to Dashboard
                </Button>
              </>
            )}

            {(status === "error" || status === "expired" || status === "timeout") && (
              <>
                <div className="w-20 h-20 rounded-full bg-accent-error/20 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-10 h-10 text-accent-error" />
                </div>
                <h2 className="font-heading font-bold text-xl text-text-primary mb-2">
                  {status === "expired" ? "Payment Expired" : "Payment Issue"}
                </h2>
                <p className="text-text-secondary mb-6">
                  {status === "expired"
                    ? "Your payment session has expired. Please try again."
                    : "There was an issue processing your payment. Please try again."}
                </p>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="w-full bg-primary hover:bg-primary-hover text-white"
                >
                  Return to Dashboard
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
